/**
 * BrainContextFlow — full-width live brain-flow canvas.
 *
 * Four bands: Brain inputs → gbrain → unified agent (capabilities) → Delivery.
 * Edges animate (flowing dashes) only while a live task is using that
 * transmission layer, or during historic-run replay. Hover/tap an edge
 * for detail; cards show inline tickers — no feed column, no slide-over.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
  ReactFlowProvider,
  getBezierPath,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import {
  AGENT_NODE,
  EDGE_IDS,
  FLOW_CRM_SURFACES,
  FLOW_OUTPUTS,
  FLOW_REQUESTER,
  FLOW_SKILLS,
  FLOW_SOURCES,
  FLOW_TYPE_LABELS,
  GBRAIN_NODE,
  describePath,
  hasReportedSkill,
  reportedSkillIds,
  resolveFlowPath,
  resolveSkillId,
  edgeKeysForPath,
  skillById,
  type FlowCrmAccess,
  type FlowCrmSurfaceId,
  type FlowDeliveryId,
  type FlowNodeInfo,
  type FlowPath,
  type FlowSourceId,
} from '../../utils/contextFlowMapping';
import { isPollEvent } from '../../utils/agentActivityHumanize';
import type { GbrainSkillRef } from '../../utils/gbrainSkillsApi';
import {
  AlertTriangle,
  Brain,
  Calendar,
  FileText,
  FolderOpen,
  History,
  LogIn,
  Mail,
  MessageSquare,
  NotebookPen,
  Search,
  Sparkles,
  UserCheck,
  Video,
} from 'lucide-react';
import './BrainContextFlow.css';

/** Delay before opening capability Skills / Last-run popovers. */
const HOVER_OPEN_MS = 2000;

// ── Public types ───────────────────────────────────────────

export interface FlowFeedItem {
  id: string;
  ts: Timestamp | null;
  agentId: string;
  sentence: string;
  status: string;
  kind: string;
  rawAction?: string;
  source?: string;
  params?: Record<string, string | number | boolean> | null;
  detail?: string | null;
}

export interface FlowSkillState {
  working: boolean;
  lastSentence: string | null;
  lastTs: Timestamp | null;
  withName: string | null;
}

export type FlowLens =
  | { mode: 'entity'; entityType: 'lead' | 'deal' | 'person'; id: string; label: string }
  | { mode: 'topic'; text: string };

export function itemMatchesLens(item: FlowFeedItem, lens: FlowLens | null): boolean {
  if (!lens) return true;
  const hay = [
    item.sentence,
    item.rawAction || '',
    item.detail || '',
    ...Object.entries(item.params || {}).map(([k, v]) => `${k} ${v}`),
  ]
    .join(' ')
    .toLowerCase();
  if (lens.mode === 'topic') {
    const needles = lens.text.toLowerCase().split(/\s+/).filter(Boolean);
    return needles.every(n => hay.includes(n));
  }
  return hay.includes(lens.id.toLowerCase()) || hay.includes(lens.label.toLowerCase());
}

interface BrainContextFlowProps {
  items: FlowFeedItem[];
  skillStates: Map<string, FlowSkillState>;
  lens: FlowLens | null;
  /** CompanyBrain SKILL.md packages keyed by FLOW capability id. */
  capabilitySkills?: Map<string, GbrainSkillRef[]>;
  capabilitySkillsLoading?: boolean;
  capabilitySkillsError?: string | null;
  /** Capability id to visually emphasize (from brain search). */
  highlightCapabilityId?: string | null;
}

// ── Icons / layout ─────────────────────────────────────────

const SOURCE_ICONS: Record<FlowSourceId, React.ReactNode> = {
  fathom: <Video size={15} />,
  emails: <Mail size={15} />,
  calendar: <Calendar size={15} />,
  enrichments: <Search size={15} />,
  crm: <FolderOpen size={15} />,
};

const DELIVERY_ICONS: Record<FlowDeliveryId, React.ReactNode> = {
  requester: <UserCheck size={15} />,
  slack: <MessageSquare size={15} />,
  drafts: <FileText size={15} />,
  crmWrite: <NotebookPen size={15} />,
};

const SRC_X = 0;
const BRAIN_X = 360;
const AGENT_X = 740;
const DEL_X = 1200;
/** Compact brain-input chips — not peer “system” cards. */
const SRC_Y = 12;
const SRC_H = 92;
/** CRM card — header + category table (5 rows). */
const SRC_H_CRM = 228;
const SRC_GAP = 14;

function sourceCardHeight(id: FlowSourceId): number {
  return id === 'crm' ? SRC_H_CRM : SRC_H;
}
/**
 * Unified agent top aligns with the first brain-input card.
 * Requesting teammate sits well above that — inbound ask, not part of the run flow.
 */
const REQ_H = 118;
const REQ_GAP = 88;
const AGENT_Y = SRC_Y;
const REQ_Y = AGENT_Y - REQ_GAP - REQ_H;
/** Completion output cards — keep pitch ≥ CSS height. */
const DEL_H = 128;
const DEL_GAP = 14;

function relativeTime(ts: Timestamp | null, now = Date.now()): string {
  if (!ts) return '—';
  const ms = now - ts.toMillis();
  if (ms < 10_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

/** Compact chip label: 32s / 1h / — */
function compactRelativeTime(ts: Timestamp | null, now = Date.now()): string {
  if (!ts) return '—';
  const ms = now - ts.toMillis();
  if (ms < 10_000) return 'now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}

/** Ticks locally so relative labels update without rebuilding the React Flow graph. */
const CompactRelativeTime: React.FC<{ ts: Timestamp | null; className?: string; title?: string }> = ({
  ts,
  className,
  title,
}) => {
  const [label, setLabel] = useState(() => compactRelativeTime(ts));
  useEffect(() => {
    const tick = () => setLabel(compactRelativeTime(ts));
    tick();
    if (!ts) return;
    const id = window.setInterval(tick, 10_000);
    return () => window.clearInterval(id);
  }, [ts]);
  return (
    <span className={className} title={title}>
      {label}
    </span>
  );
};

/** True until `untilMs`, then flips off locally (no parent clock). */
function useLiveUntil(untilMs: number): boolean {
  const [live, setLive] = useState(() => untilMs > Date.now());
  useEffect(() => {
    const left = untilMs - Date.now();
    if (left <= 0) {
      setLive(false);
      return;
    }
    setLive(true);
    const t = window.setTimeout(() => setLive(false), left);
    return () => window.clearTimeout(t);
  }, [untilMs]);
  return live;
}

const REPLAY_MS = 2500;
const RUNS_PER_SKILL = 5;

interface SkillRunRow {
  id: string;
  sentence: string;
  ts: Timestamp | null;
  path: FlowPath;
  brainPath?: string | null;
  brainExcerpt?: string | null;
}

/** Canvas-local capability run menu (rendered outside React Flow nodes). */
interface SkillMenuState {
  skillId: string;
  label: string;
  accent: string;
  /** Anchor rect relative to the canvas wrapper. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Prefer opening above the anchor when true. */
  openUp: boolean;
}

/** Canvas-local CompanyBrain skills list for a capability. */
interface CapabilitySkillsMenuState {
  skillId: string;
  label: string;
  accent: string;
  packages: GbrainSkillRef[];
  x: number;
  y: number;
  w: number;
  h: number;
  openUp: boolean;
}

interface ActivityMomentRow {
  id: string;
  sentence: string;
  ts: Timestamp | null;
  kind: string;
  sourceLabel: string | null;
}

/** Canvas-local activity list (gbrain / requesting teammate). */
interface ActivityMenuState {
  moments: ActivityMomentRow[];
  x: number;
  y: number;
  w: number;
  h: number;
}

const ACTIVITY_MOMENTS_CAP = 20;

// ── Glow edge ──────────────────────────────────────────────

interface GlowEdgeData {
  /**
   * Epoch ms until which this edge should show the flowing-dash animation.
   * Only set for an in-flight pulse (live task using this layer) or historic replay.
   * Evaluated inside the edge so the parent graph doesn’t rebuild on a clock.
   */
  liveUntil: number;
  locked: boolean;
  accent: string;
  label?: string;
  /** Live CRM pull — idle uses a light dash so path type reads on the canvas. */
  live?: boolean;
  onHover: (edgeId: string, x: number, y: number) => void;
  onLeave: (edgeId: string) => void;
  onTap: (edgeId: string, x: number, y: number) => void;
}

const GlowFlowEdge: React.FC<EdgeProps<GlowEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}) => {
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const active = useLiveUntil(data?.liveUntil || 0);
  const locked = !!data?.locked;
  const live = !!data?.live;
  const stroke = live ? '#a8a59c' : '#c8c5bc';
  /** Live pulls: light dash when idle; flowing dashes when active. Snapshot: solid → flow. */
  const dasharray = active ? (live ? '4 8' : '7 10') : live ? '2 6' : undefined;

  const handlePointer = (e: React.MouseEvent | React.TouchEvent) => {
    const point =
      'touches' in e
        ? e.touches[0] || e.changedTouches[0]
        : (e as React.MouseEvent);
    if (!point) return;
    data?.onHover(id, point.clientX, point.clientY);
  };

  return (
    <>
      {/* Invisible hit target for hover / tap */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        className="cf-edge-hit"
        onMouseEnter={handlePointer}
        onMouseMove={handlePointer}
        onMouseLeave={() => data?.onLeave(id)}
        onClick={e => {
          e.stopPropagation();
          data?.onTap(id, e.clientX, e.clientY);
        }}
        onTouchEnd={e => {
          e.stopPropagation();
          const t = e.changedTouches[0];
          if (t) data?.onTap(id, t.clientX, t.clientY);
        }}
        style={{ cursor: 'pointer' }}
      />
      <path
        id={id}
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={live ? 0.7 : 0.8}
        style={{ strokeWidth: live ? 0.7 : 0.8 }}
        strokeLinecap="round"
        strokeDasharray={dasharray}
        className={`react-flow__edge-path${active ? ' cf-edge-flow' : ''}${locked ? ' cf-edge-locked' : ''}${live ? ' cf-edge-live' : ''}`}
      />
      {data?.label && (
        <EdgeLabelRenderer>
          <div
            className={`cf-edge-label${active || locked ? ' lit' : ''}${live ? ' live' : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'none',
            }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const EDGE_TYPES = { glowFlow: GlowFlowEdge };

// ── Custom nodes (inline tickers, no popups) ───────────────

interface CrmSurfaceRow {
  id: FlowCrmSurfaceId;
  label: string;
  access: FlowCrmAccess;
  lastTs: Timestamp | null;
  glowUntil: number;
}

interface SourceNodeData {
  info: FlowNodeInfo;
  count: number | null;
  countLabel: string;
  glowUntil: number;
  ticker: string | null;
  tickerTs: Timestamp | null;
  /** CRM only — category table rows with per-row handles. */
  crmRows?: CrmSurfaceRow[];
}

const SourceNode: React.FC<NodeProps<SourceNodeData>> = ({ data }) => {
  const isCrm = data.info.id === 'crm' && !!data.crmRows?.length;
  const pulsing = useLiveUntil(data.glowUntil || 0);

  return (
    <div
      className={`cf-topic${isCrm ? ' cf-topic-crm' : ''}${pulsing ? ' pulsing' : ''}${data.ticker ? ' has-ticker' : ''}`}
      style={{ ['--cf-accent' as any]: data.info.accent }}
      title={data.info.detail}
    >
      <span className="cf-input-mark" title="Brain input" aria-label="Brain input">
        <LogIn size={11} strokeWidth={1.75} />
      </span>
      <div className="cf-node-head">
        <span className="cf-topic-icon">{SOURCE_ICONS[data.info.id as FlowSourceId]}</span>
        <span className="cf-topic-label">{data.info.label}</span>
        {data.count !== null && (
          <span className="cf-node-count" title={data.countLabel}>
            {data.count}
          </span>
        )}
      </div>
      {!isCrm && <p className="cf-topic-desc">{data.info.description}</p>}
      {isCrm && data.crmRows && (
        <div className="cf-crm-table" aria-label="CRM data categories accessible to gbrain">
          <div className="cf-crm-table-head">
            <span>Category</span>
            <span>Path</span>
            <span className="cf-crm-col-when">Last</span>
          </div>
          <ul className="cf-crm-table-body">
            {data.crmRows.map(row => (
              <CrmSurfaceRowView key={row.id} row={row} />
            ))}
          </ul>
        </div>
      )}
      {!isCrm && data.ticker && (
        <div className="cf-topic-ticker">
          <span className="cf-ticker-text">{data.ticker}</span>
          {data.tickerTs && <CompactRelativeTime ts={data.tickerTs} className="cf-ticker-when" />}
        </div>
      )}
      {!isCrm && <Handle type="source" position={Position.Right} className="cf-handle" />}
    </div>
  );
};

const CrmSurfaceRowView: React.FC<{ row: CrmSurfaceRow }> = ({ row }) => {
  const active = useLiveUntil(row.glowUntil);
  return (
    <li
      className={`cf-crm-row${active ? ' active' : ''}${row.access === 'live' ? ' live' : ''}`}
    >
      <span className="cf-crm-cat">{row.label}</span>
      <span
        className="cf-crm-path"
        title={
          row.access === 'snapshot'
            ? 'Synced into CompanyBrain on the Mac mini'
            : 'Typed retrieval from the CRM when the agent needs it'
        }
      >
        {row.access === 'snapshot' ? 'In brain' : 'Live pull'}
      </span>
      <CompactRelativeTime ts={row.lastTs} className="cf-crm-when" />
      {/* Nested in the row so the spline anchors on the row’s vertical center */}
      <Handle
        type="source"
        id={row.id}
        position={Position.Right}
        className={`cf-handle cf-crm-row-handle${active ? ' lit' : ''}`}
      />
    </li>
  );
};

interface BrainNodeData {
  info: FlowNodeInfo;
  glowUntil: number;
  inCount: number;
  ticker: string | null;
  tickerTs: Timestamp | null;
  moments: ActivityMomentRow[];
  onBrainMenu: (menu: ActivityMenuState | null) => void;
  onBrainMenuLeave: () => void;
  getCanvasEl: () => HTMLDivElement | null;
}

const BrainNode: React.FC<NodeProps<BrainNodeData>> = ({ data }) => {
  const pulsing = useLiveUntil(data.glowUntil || 0);
  const openMenu = (el: HTMLElement) => {
    const card = el.getBoundingClientRect();
    const canvas = data.getCanvasEl()?.getBoundingClientRect();
    const x = canvas ? card.left - canvas.left : card.left;
    const y = canvas ? card.top - canvas.top : card.top;
    data.onBrainMenu({
      moments: data.moments,
      x,
      y,
      w: card.width,
      h: card.height,
    });
  };

  return (
    <div
      className={`cf-node cf-brain nodrag nopan${pulsing ? ' pulsing' : ''}${data.inCount > 0 ? ' has-moments' : ''}`}
      onPointerDown={e => e.stopPropagation()}
      onMouseEnter={e => {
        if (data.inCount > 0) openMenu(e.currentTarget);
      }}
      onMouseLeave={() => data.onBrainMenuLeave()}
      onClick={e => {
        e.stopPropagation();
        if (data.inCount > 0) openMenu(e.currentTarget);
      }}
    >
      <div className="cf-brain-head">
        <Brain size={18} />
        <span className="cf-brain-title">gbrain</span>
      </div>
      <span className="cf-brain-sub">md files + index · Mac mini</span>
      <p className="cf-node-desc">{data.info.description}</p>
      <div className={`cf-ticker${data.ticker ? '' : ' empty'}`}>
        {data.ticker ? (
          <>
            <span className="cf-ticker-text">{data.ticker}</span>
            {data.tickerTs && <CompactRelativeTime ts={data.tickerTs} className="cf-ticker-when" />}
          </>
        ) : (
          <span className="cf-ticker-quiet">listening…</span>
        )}
      </div>
      <span className="cf-brain-stat" title={data.inCount > 0 ? 'Hover to see moments' : undefined}>
        {data.inCount > 0 ? `${data.inCount} context moments in view` : 'waiting for context'}
      </span>
      {data.info.honestGap && <span className="cf-honest-note">{data.info.honestGap}</span>}
      <Handle type="target" position={Position.Left} className="cf-handle" />
      <Handle type="source" position={Position.Right} className="cf-handle" />
    </div>
  );
};

interface AgentNodeData {
  info: FlowNodeInfo;
  anyWorking: boolean;
  skills: Array<{
    id: string;
    label: string;
    accent: string;
    working: boolean;
    glowUntil: number;
    focused: boolean;
    lastSentence: string | null;
    lastTs: Timestamp | null;
    runs: SkillRunRow[];
    packages: GbrainSkillRef[];
  }>;
  capabilitySkillsLoading?: boolean;
  onSkillMenu: (menu: SkillMenuState | null) => void;
  onSkillMenuLeave: () => void;
  onCapabilitySkillsMenu: (menu: CapabilitySkillsMenuState | null) => void;
  onCapabilitySkillsMenuLeave: () => void;
  getCanvasEl: () => HTMLDivElement | null;
}

const CapabilityRow: React.FC<{
  s: AgentNodeData['skills'][number];
  openUp: boolean;
  capabilitySkillsLoading?: boolean;
  openRunsFor: (s: AgentNodeData['skills'][number], el: HTMLElement, openUp: boolean) => void;
  openPackagesFor: (s: AgentNodeData['skills'][number], el: HTMLElement, openUp: boolean) => void;
  scheduleOpen: (fn: () => void) => void;
  clearOpenTimer: () => void;
  onSkillMenuLeave: () => void;
  onCapabilitySkillsMenuLeave: () => void;
}> = ({
  s,
  openUp,
  capabilitySkillsLoading,
  openRunsFor,
  openPackagesFor,
  scheduleOpen,
  clearOpenTimer,
  onSkillMenuLeave,
  onCapabilitySkillsMenuLeave,
}) => {
  const pulsing = useLiveUntil(s.glowUntil);
  const pkgCount = s.packages.length;
  const skillsLabel = capabilitySkillsLoading ? '…' : pkgCount > 0 ? String(pkgCount) : '—';
  return (
    <div
      className={`cf-skill-row nodrag nopan${s.working ? ' working' : ''}${pulsing ? ' pulsing' : ''}${s.focused ? ' traced' : ''}`}
      style={{ ['--cf-accent' as any]: s.accent }}
    >
      <span className="cf-skill-dot" />
      <span className="cf-skill-label">{s.label}</span>
      <button
        type="button"
        className="cf-cap-skills-cell nodrag nopan"
        title={
          pkgCount > 0
            ? `${pkgCount} CompanyBrain skill${pkgCount === 1 ? '' : 's'} — hover 2s`
            : capabilitySkillsLoading
              ? 'Loading skills from CompanyBrain…'
              : 'No CompanyBrain skills mapped yet'
        }
        aria-haspopup="dialog"
        aria-label={`${s.label} skills`}
        onPointerDown={e => e.stopPropagation()}
        onMouseEnter={e => {
          const el = e.currentTarget;
          scheduleOpen(() => openPackagesFor(s, el, openUp));
        }}
        onMouseLeave={() => {
          clearOpenTimer();
          onCapabilitySkillsMenuLeave();
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          clearOpenTimer();
          openPackagesFor(s, e.currentTarget, openUp);
        }}
      >
        {skillsLabel}
      </button>
      <button
        type="button"
        className="cf-skill-when nodrag nopan"
        title={
          s.lastSentence
            ? `Last: ${s.lastSentence} — hover 2s for log`
            : s.lastTs
              ? `${relativeTime(s.lastTs)} — hover 2s for log`
              : 'No runs yet — hover 2s for log'
        }
        aria-haspopup="dialog"
        aria-label={`${s.label} recent runs`}
        onPointerDown={e => e.stopPropagation()}
        onMouseEnter={e => {
          const el = e.currentTarget;
          scheduleOpen(() => openRunsFor(s, el, openUp));
        }}
        onMouseLeave={() => {
          clearOpenTimer();
          onSkillMenuLeave();
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          clearOpenTimer();
          openRunsFor(s, e.currentTarget, openUp);
        }}
      >
        <CompactRelativeTime ts={s.lastTs} />
      </button>
    </div>
  );
};

function anchorRect(
  el: HTMLElement,
  getCanvasEl: () => HTMLDivElement | null,
): { x: number; y: number; w: number; h: number } {
  const chip = el.getBoundingClientRect();
  const canvas = getCanvasEl()?.getBoundingClientRect();
  return {
    x: canvas ? chip.left - canvas.left : chip.left,
    y: canvas ? chip.top - canvas.top : chip.top,
    w: chip.width,
    h: chip.height,
  };
}

const AgentNode: React.FC<NodeProps<AgentNodeData>> = ({ data }) => {
  const openTimerRef = useRef<number | null>(null);

  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  useEffect(() => () => clearOpenTimer(), []);

  const openRunsFor = (s: AgentNodeData['skills'][number], el: HTMLElement, openUp: boolean) => {
    const a = anchorRect(el, data.getCanvasEl);
    data.onSkillMenu({
      skillId: s.id,
      label: s.label,
      accent: s.accent,
      ...a,
      openUp,
    });
  };

  const openPackagesFor = (s: AgentNodeData['skills'][number], el: HTMLElement, openUp: boolean) => {
    const a = anchorRect(el, data.getCanvasEl);
    data.onCapabilitySkillsMenu({
      skillId: s.id,
      label: s.label,
      accent: s.accent,
      packages: s.packages,
      ...a,
      openUp,
    });
  };

  const scheduleOpen = (fn: () => void) => {
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      fn();
    }, HOVER_OPEN_MS);
  };

  return (
    <div className="cf-node cf-agent">
      <div className="cf-agent-head">
        <Sparkles size={16} />
        <span className="cf-agent-title">{data.info.label}</span>
        <span className={`cf-agent-state ${data.anyWorking ? 'working' : 'idle'}`}>
          <span className="cf-state-dot" />
          {data.anyWorking ? 'Working' : 'Idle'}
        </span>
      </div>
      <p className="cf-node-desc cf-agent-desc">{data.info.description}</p>
      <div className="cf-skills cf-skills-table" aria-label="Agent capabilities">
        <div className="cf-skills-table-head">
          <span className="cf-skills-col-cap">Capabilities</span>
          <span className="cf-skills-col-pkg">Skills</span>
          <span className="cf-skills-col-when">Last</span>
        </div>
        <ul className="cf-skills-table-body">
          {data.skills.map((s, i) => {
            const openUp = i >= Math.ceil(data.skills.length / 2);
            return (
              <li key={s.id}>
                <CapabilityRow
                  s={s}
                  openUp={openUp}
                  capabilitySkillsLoading={data.capabilitySkillsLoading}
                  openRunsFor={openRunsFor}
                  openPackagesFor={openPackagesFor}
                  scheduleOpen={scheduleOpen}
                  clearOpenTimer={clearOpenTimer}
                  onSkillMenuLeave={data.onSkillMenuLeave}
                  onCapabilitySkillsMenuLeave={data.onCapabilitySkillsMenuLeave}
                />
              </li>
            );
          })}
        </ul>
      </div>
      {/* Asks arrive from the Requesting teammate card above */}
      <Handle type="target" id="req" position={Position.Top} className="cf-handle" />
      <Handle type="target" id="in" position={Position.Left} className="cf-handle" style={{ top: '42%' }} />
      {/* Completion outputs fan out to the right */}
      <Handle type="source" id="out-slack" position={Position.Right} className="cf-handle" style={{ top: '28%' }} />
      <Handle type="source" id="out-drafts" position={Position.Right} className="cf-handle" style={{ top: '52%' }} />
      <Handle type="source" id="out-crmWrite" position={Position.Right} className="cf-handle" style={{ top: '76%' }} />
    </div>
  );
};

interface DeliveryNodeData {
  info: FlowNodeInfo;
  count: number | null;
  glowUntil: number;
  ticker: string | null;
  tickerTs: Timestamp | null;
  /** Requesting teammate — logged asks / handbacks in the loaded window. */
  moments?: ActivityMomentRow[];
  onRequesterMenu?: (menu: ActivityMenuState | null) => void;
  onRequesterMenuLeave?: () => void;
  getCanvasEl?: () => HTMLDivElement | null;
}

const DeliveryNode: React.FC<NodeProps<DeliveryNodeData>> = ({ data }) => {
  const isRequester = data.info.id === 'requester';
  const hasMoments = isRequester && (data.count || 0) > 0;
  const pulsing = useLiveUntil(data.glowUntil || 0);

  const openRequesterMenu = (el: HTMLElement) => {
    if (!data.onRequesterMenu || !data.getCanvasEl) return;
    const card = el.getBoundingClientRect();
    const canvas = data.getCanvasEl()?.getBoundingClientRect();
    const x = canvas ? card.left - canvas.left : card.left;
    const y = canvas ? card.top - canvas.top : card.top;
    data.onRequesterMenu({
      moments: data.moments || [],
      x,
      y,
      w: card.width,
      h: card.height,
    });
  };

  return (
    <div
      className={`cf-node cf-delivery${isRequester ? ' cf-requester' : ''}${hasMoments ? ' has-moments' : ''}${pulsing ? ' pulsing' : ''}${data.ticker ? ' has-ticker' : ''}`}
      style={{ ['--cf-accent' as any]: data.info.accent }}
      onPointerDown={isRequester ? e => e.stopPropagation() : undefined}
      onMouseEnter={
        hasMoments
          ? e => openRequesterMenu(e.currentTarget)
          : undefined
      }
      onMouseLeave={hasMoments ? () => data.onRequesterMenuLeave?.() : undefined}
      onClick={
        hasMoments
          ? e => {
              e.stopPropagation();
              openRequesterMenu(e.currentTarget);
            }
          : undefined
      }
      title={hasMoments ? 'Hover to see logged activity' : undefined}
    >
      {isRequester && <span className="cf-requester-mark">Before the run</span>}
      <div className="cf-node-head">
        <span className="cf-node-icon">{DELIVERY_ICONS[data.info.id as FlowDeliveryId]}</span>
        <span className="cf-node-label">{data.info.label}</span>
        {data.count !== null && data.count > 0 && <span className="cf-node-count">{data.count}</span>}
      </div>
      <p className="cf-node-desc">{data.info.description}</p>
      <div className={`cf-ticker${data.ticker ? '' : ' empty'}`}>
        {data.ticker ? (
          <>
            <span className="cf-ticker-text">{data.ticker}</span>
            {data.tickerTs && <CompactRelativeTime ts={data.tickerTs} className="cf-ticker-when" />}
          </>
        ) : (
          <span className="cf-ticker-quiet">quiet</span>
        )}
      </div>
      {isRequester ? (
        <Handle type="source" id="to-agent" position={Position.Bottom} className="cf-handle" />
      ) : (
        <Handle type="target" position={Position.Left} className="cf-handle" />
      )}
    </div>
  );
};

const NODE_TYPES = {
  cfSource: SourceNode,
  cfBrain: BrainNode,
  cfAgent: AgentNode,
  cfDelivery: DeliveryNode,
};

// ── Entity lens data ───────────────────────────────────────

type ArtifactType = 'meetings' | 'emails' | 'activities' | 'documents';

const ARTIFACT_META: Record<ArtifactType, { label: string; accent: string; sourceId: FlowSourceId }> = {
  meetings: { label: 'Meeting notes', accent: '#4A6670', sourceId: 'fathom' },
  emails: { label: 'Linked emails', accent: '#2C3E2F', sourceId: 'emails' },
  activities: { label: 'Activities & events', accent: '#3D5A40', sourceId: 'calendar' },
  documents: { label: 'Documents & designs', accent: '#6B6960', sourceId: 'crm' },
};

interface EntityContext {
  counts: Partial<Record<ArtifactType, number>>;
  hasProposal: boolean;
  timeline: Array<{ month: string; counts: Partial<Record<ArtifactType, number>> }>;
  loading: boolean;
  error: string | null;
}

function tsToDate(v: unknown): Date | null {
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

async function loadEntityContext(
  entityType: 'lead' | 'deal',
  id: string,
): Promise<Omit<EntityContext, 'loading' | 'error'>> {
  const dates: Array<{ type: ArtifactType; date: Date }> = [];
  const counts: Partial<Record<ArtifactType, number>> = {};
  let hasProposal = false;
  const jobs: Promise<void>[] = [];

  const countAndDates = (
    type: ArtifactType,
    col: string,
    field: string,
    dateFields: string[],
  ) => {
    jobs.push(
      (async () => {
        const base = query(collection(db, col), where(field, '==', id));
        const [countSnap, docsSnap] = await Promise.all([
          getCountFromServer(base),
          getDocs(query(collection(db, col), where(field, '==', id), limit(400))),
        ]);
        counts[type] = (counts[type] || 0) + countSnap.data().count;
        for (const d of docsSnap.docs) {
          const data = d.data() as Record<string, unknown>;
          for (const f of dateFields) {
            const date = tsToDate(data[f]);
            if (date) {
              dates.push({ type, date });
              break;
            }
          }
        }
      })().catch(() => undefined),
    );
  };

  if (entityType === 'deal') {
    countAndDates('meetings', 'projectMeetingNotes', 'dealId', ['meetingAt', 'createdAt']);
    countAndDates('documents', 'leadDocuments', 'dealId', ['uploadedAt', 'createdAt']);
    jobs.push(
      getDoc(doc(db, 'proposalBuilders', id))
        .then(snap => {
          hasProposal = snap.exists();
        })
        .catch(() => undefined),
    );
  } else {
    countAndDates('documents', 'leadDocuments', 'leadId', ['uploadedAt', 'createdAt']);
  }
  countAndDates('emails', 'emailMessages', 'relatedTo.entityId', ['createdAt', 'sentAt']);
  countAndDates('activities', 'activities', 'entityId', ['createdAt', 'scheduledAt', 'completedAt']);

  await Promise.all(jobs);

  const byMonth = new Map<string, Partial<Record<ArtifactType, number>>>();
  for (const { type, date } of dates) {
    const month = date.toISOString().slice(0, 7);
    const bucket = byMonth.get(month) || {};
    bucket[type] = (bucket[type] || 0) + 1;
    byMonth.set(month, bucket);
  }
  const timeline = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, c]) => ({ month, counts: c }));

  return { counts, hasProposal, timeline };
}

// ── Main ───────────────────────────────────────────────────

const PULSE_MS = 3200;
/**
 * Fit once on mount and when the window size changes — not on every
 * nodes/edges update (that causes the canvas to blink/jump).
 */
const FitViewOnResize: React.FC = () => {
  const { fitView } = useReactFlow();
  const fittedRef = useRef(false);

  useEffect(() => {
    let debounce: number | null = null;
    const run = () => {
      fitView({ padding: 0.07, duration: fittedRef.current ? 180 : 0 });
      fittedRef.current = true;
    };
    const t = window.setTimeout(run, 50);
    const onResize = () => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(run, 120);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(t);
      if (debounce) window.clearTimeout(debounce);
      window.removeEventListener('resize', onResize);
    };
  }, [fitView]);

  return null;
};

interface EdgeTooltipState {
  edgeId: string;
  x: number;
  y: number;
  locked: boolean;
}

const BrainContextFlowInner: React.FC<BrainContextFlowProps> = ({
  items,
  skillStates,
  lens,
  capabilitySkills,
  capabilitySkillsLoading,
  highlightCapabilityId,
}) => {
  const [pulses, setPulses] = useState<Map<string, number>>(new Map());
  const [entityCtx, setEntityCtx] = useState<EntityContext | null>(null);
  const [skillMenu, setSkillMenu] = useState<SkillMenuState | null>(null);
  const [packagesMenu, setPackagesMenu] = useState<CapabilitySkillsMenuState | null>(null);
  const [brainMenu, setBrainMenu] = useState<ActivityMenuState | null>(null);
  const [requesterMenu, setRequesterMenu] = useState<ActivityMenuState | null>(null);
  const [focusedSkillId, setFocusedSkillId] = useState<string | null>(null);
  const [replayEdgeKeys, setReplayEdgeKeys] = useState<Set<string> | null>(null);
  const [historicReplay, setHistoricReplay] = useState<{
    skillId: string;
    label: string;
    accent: string;
    sentence: string;
    when: string;
  } | null>(null);
  const [tooltip, setTooltip] = useState<EdgeTooltipState | null>(null);
  const prevNewestRef = useRef<string | null>(null);
  const replayTimerRef = useRef<number | null>(null);
  const skillLeaveTimer = useRef<number | null>(null);
  const packagesLeaveTimer = useRef<number | null>(null);
  const brainLeaveTimer = useRef<number | null>(null);
  const requesterLeaveTimer = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const openSkillId = skillMenu?.skillId ?? null;

  useEffect(() => {
    if (!skillMenu && !packagesMenu && !brainMenu && !requesterMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSkillMenu(null);
        setPackagesMenu(null);
        setBrainMenu(null);
        setRequesterMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skillMenu, packagesMenu, brainMenu, requesterMenu]);

  useEffect(
    () => () => {
      if (skillLeaveTimer.current) window.clearTimeout(skillLeaveTimer.current);
      if (packagesLeaveTimer.current) window.clearTimeout(packagesLeaveTimer.current);
      if (brainLeaveTimer.current) window.clearTimeout(brainLeaveTimer.current);
      if (requesterLeaveTimer.current) window.clearTimeout(requesterLeaveTimer.current);
    },
    [],
  );

  useEffect(
    () => () => {
      if (replayTimerRef.current) window.clearTimeout(replayTimerRef.current);
    },
    [],
  );

  const pathItems = useMemo(() => {
    const out: Array<{ item: FlowFeedItem; path: FlowPath }> = [];
    for (const item of items) {
      if (lens && !itemMatchesLens(item, lens)) continue;
      const path = resolveFlowPath(item.rawAction || '', item.agentId, item.source, item.params);
      out.push({ item, path });
    }
    return out;
  }, [items, lens]);

  /**
   * Recent moments per skill (newest first, capped).
   * Uses the same skill attribution as skillStates (explicit skillsUsed + agent fold)
   * so a chip that shows “3m” always has rows to open — not only path.skillId.
   */
  const runsBySkill = useMemo(() => {
    const map = new Map<string, SkillRunRow[]>();
    for (const { item, path } of pathItems) {
      if (item.rawAction && isPollEvent(item.rawAction, item.source)) continue;
      // True queue polls from path classification — skip. Keep report/deliver/request/etc.
      if (path.type === 'poll') continue;

      const explicit = reportedSkillIds(item.params);
      const skillIds = explicit.length
        ? explicit
        : ([path.skillId || resolveSkillId(item.agentId, item.params)].filter(Boolean) as string[]);
      if (!skillIds.length) continue;

      for (const skillId of skillIds) {
        const list = map.get(skillId) || [];
        if (list.length >= RUNS_PER_SKILL) continue;
        // One row per event per skill (avoid dupes if listed twice).
        if (list.some(r => r.id === item.id)) continue;
        list.push({
          id: item.id,
          sentence: item.sentence,
          ts: item.ts,
          path,
          brainPath:
            typeof item.params?.brainPath === 'string' ? item.params.brainPath : null,
          brainExcerpt:
            typeof item.params?.brainExcerpt === 'string' ? item.params.brainExcerpt : null,
        });
        map.set(skillId, list);
      }
    }
    return map;
  }, [pathItems]);

  /** Latest non-poll event per edge key (items are newest-first). */
  const edgeLatestByKey = useMemo(() => {
    const map = new Map<string, { item: FlowFeedItem; path: FlowPath }>();
    for (const row of pathItems) {
      if (row.path.type === 'poll' || row.path.type === 'report') continue;
      for (const key of edgeKeysForPath(row.path)) {
        if (!map.has(key)) map.set(key, row);
      }
    }
    return map;
  }, [pathItems]);

  // Live pulse engine — refresh flow timer when more traffic hits the same edge.
  useEffect(() => {
    const newest = items[0]?.id ?? null;
    const prev = prevNewestRef.current;
    prevNewestRef.current = newest;
    if (!newest || newest === prev || prev === null) return;

    const freshIds = new Set<string>();
    for (const it of items) {
      if (it.id === prev) break;
      freshIds.add(it.id);
      if (freshIds.size >= 15) break;
    }
    if (!freshIds.size) return;

    const expiry = Date.now() + PULSE_MS;
    const additions = new Map<string, number>();
    for (const { item, path } of pathItems) {
      if (!freshIds.has(item.id)) continue;
      if (path.type === 'poll') continue;
      for (const key of edgeKeysForPath(path)) additions.set(key, expiry);
      if (path.skillId) additions.set(`skill-${path.skillId}`, expiry);
      additions.set('node-gbrain', expiry);
      if (path.sourceId) additions.set(`node-src-${path.sourceId}`, expiry);
      if (path.deliveryId) additions.set(`node-del-${path.deliveryId}`, expiry);
    }
    if (!additions.size) return;

    setPulses(prevMap => {
      const next = new Map(prevMap);
      for (const [k, exp] of Array.from(additions)) next.set(k, exp);
      return next;
    });
    const t = setTimeout(() => {
      setPulses(prevMap => new Map(Array.from(prevMap).filter(([, exp]) => exp > Date.now())));
    }, PULSE_MS + 200);
    return () => clearTimeout(t);
  }, [items, pathItems]);

  /**
   * Epoch ms until which an edge/node should animate.
   * Live pulses only — not “had traffic in the last few minutes.”
   * Historic replay writes into `pulses` (and `replayEdgeKeys`) separately.
   */
  const liveUntilFor = useCallback((key: string) => pulses.get(key) || 0, [pulses]);

  /** Menu / historic replay — drives edge dimming. */
  const pathFocusSkillId = focusedSkillId || openSkillId;
  /** Includes brain-search capability highlight for the Capabilities table. */
  const highlightSkillId = pathFocusSkillId || highlightCapabilityId || null;

  const focusedEdgeKeys = useMemo(() => {
    if (replayEdgeKeys) return replayEdgeKeys;
    const skillId = pathFocusSkillId;
    if (!skillId) return new Set<string>();
    const keys = new Set<string>();
    for (const { path } of pathItems) {
      if (path.skillId !== skillId) continue;
      if (path.type === 'poll' || path.type === 'report') continue;
      for (const k of edgeKeysForPath(path)) keys.add(k);
    }
    return keys;
  }, [replayEdgeKeys, pathFocusSkillId, pathItems]);

  // Per-node tickers — intake/brain only show real context pulls (retrieve/ingest).
  const tickers = useMemo(() => {
    const bySource = new Map<FlowSourceId, { sentence: string; ts: Timestamp | null }>();
    const byDelivery = new Map<FlowDeliveryId, { sentence: string; ts: Timestamp | null }>();
    let brain: { sentence: string; ts: Timestamp | null } | null = null;
    for (const { item, path } of pathItems) {
      if (path.type === 'poll' || path.type === 'report') continue;
      if (
        (path.type === 'retrieve' || path.type === 'ingest') &&
        path.sourceId &&
        !bySource.has(path.sourceId)
      ) {
        bySource.set(path.sourceId, { sentence: item.sentence, ts: item.ts });
      }
      if (path.deliveryId && !byDelivery.has(path.deliveryId)) {
        byDelivery.set(path.deliveryId, { sentence: item.sentence, ts: item.ts });
      }
      if (
        path.pulseRequester &&
        path.deliveryId !== 'requester' &&
        !byDelivery.has('requester')
      ) {
        byDelivery.set('requester', { sentence: item.sentence, ts: item.ts });
      }
      if (!brain && (path.type === 'ingest' || path.type === 'retrieve')) {
        brain = { sentence: item.sentence, ts: item.ts };
      }
    }
    return { bySource, byDelivery, brain };
  }, [pathItems]);

  const viewCounts = useMemo(() => {
    const bySource = new Map<FlowSourceId, number>();
    const byDelivery = new Map<FlowDeliveryId, number>();
    let brainIn = 0;
    for (const { path } of pathItems) {
      if (path.type === 'poll' || path.type === 'report') continue;
      if (path.sourceId) bySource.set(path.sourceId, (bySource.get(path.sourceId) || 0) + 1);
      if (path.deliveryId) byDelivery.set(path.deliveryId, (byDelivery.get(path.deliveryId) || 0) + 1);
      if (path.pulseRequester && path.deliveryId !== 'requester') {
        byDelivery.set('requester', (byDelivery.get('requester') || 0) + 1);
      }
      if (path.type === 'ingest' || path.type === 'retrieve') brainIn += 1;
    }
    return { bySource, byDelivery, brainIn };
  }, [pathItems]);

  /** Ingest/retrieve moments that make up the gbrain “in view” count (newest first). */
  const brainMoments = useMemo(() => {
    const out: ActivityMomentRow[] = [];
    for (const { item, path } of pathItems) {
      if (path.type !== 'ingest' && path.type !== 'retrieve') continue;
      const sourceLabel = path.sourceId
        ? FLOW_SOURCES.find(s => s.id === path.sourceId)?.label || null
        : null;
      out.push({
        id: item.id,
        sentence: item.sentence,
        ts: item.ts,
        kind: FLOW_TYPE_LABELS[path.type],
        sourceLabel,
      });
      if (out.length >= ACTIVITY_MOMENTS_CAP) break;
    }
    return out;
  }, [pathItems]);

  /** Asks + handbacks that light the Requesting teammate card. */
  const requesterMoments = useMemo(() => {
    const out: ActivityMomentRow[] = [];
    for (const { item, path } of pathItems) {
      const isRequesterPath =
        path.type === 'request' ||
        path.deliveryId === 'requester' ||
        !!path.pulseRequester;
      if (!isRequesterPath) continue;
      if (path.type === 'poll' || path.type === 'report') continue;
      out.push({
        id: item.id,
        sentence: item.sentence,
        ts: item.ts,
        kind: FLOW_TYPE_LABELS[path.type],
        sourceLabel: path.skillId ? skillById(path.skillId)?.label || null : null,
      });
      if (out.length >= ACTIVITY_MOMENTS_CAP) break;
    }
    return out;
  }, [pathItems]);

  useEffect(() => {
    if (!lens || lens.mode !== 'entity' || lens.entityType === 'person') {
      setEntityCtx(null);
      return;
    }
    let cancelled = false;
    setEntityCtx({ counts: {}, hasProposal: false, timeline: [], loading: true, error: null });
    loadEntityContext(lens.entityType, lens.id)
      .then(data => {
        if (!cancelled) setEntityCtx({ ...data, loading: false, error: null });
      })
      .catch(e => {
        if (!cancelled) {
          setEntityCtx({
            counts: {},
            hasProposal: false,
            timeline: [],
            loading: false,
            error: e instanceof Error ? e.message : 'Failed to load context',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lens]);

  const onSkillMenu = useCallback((menu: SkillMenuState | null) => {
    if (skillLeaveTimer.current) {
      window.clearTimeout(skillLeaveTimer.current);
      skillLeaveTimer.current = null;
    }
    setPackagesMenu(null);
    setBrainMenu(null);
    setRequesterMenu(null);
    setSkillMenu(menu);
  }, []);

  const scheduleSkillMenuClose = useCallback(() => {
    if (skillLeaveTimer.current) window.clearTimeout(skillLeaveTimer.current);
    skillLeaveTimer.current = window.setTimeout(() => {
      setSkillMenu(null);
      skillLeaveTimer.current = null;
    }, 350);
  }, []);

  const cancelSkillMenuClose = useCallback(() => {
    if (skillLeaveTimer.current) {
      window.clearTimeout(skillLeaveTimer.current);
      skillLeaveTimer.current = null;
    }
  }, []);

  const onCapabilitySkillsMenu = useCallback((menu: CapabilitySkillsMenuState | null) => {
    if (packagesLeaveTimer.current) {
      window.clearTimeout(packagesLeaveTimer.current);
      packagesLeaveTimer.current = null;
    }
    setSkillMenu(null);
    setBrainMenu(null);
    setRequesterMenu(null);
    setPackagesMenu(menu);
  }, []);

  const schedulePackagesMenuClose = useCallback(() => {
    if (packagesLeaveTimer.current) window.clearTimeout(packagesLeaveTimer.current);
    packagesLeaveTimer.current = window.setTimeout(() => {
      setPackagesMenu(null);
      packagesLeaveTimer.current = null;
    }, 350);
  }, []);

  const cancelPackagesMenuClose = useCallback(() => {
    if (packagesLeaveTimer.current) {
      window.clearTimeout(packagesLeaveTimer.current);
      packagesLeaveTimer.current = null;
    }
  }, []);

  const onBrainMenu = useCallback((menu: ActivityMenuState | null) => {
    if (brainLeaveTimer.current) {
      window.clearTimeout(brainLeaveTimer.current);
      brainLeaveTimer.current = null;
    }
    setSkillMenu(null);
    setPackagesMenu(null);
    setRequesterMenu(null);
    setBrainMenu(menu);
  }, []);

  const scheduleBrainMenuClose = useCallback(() => {
    if (brainLeaveTimer.current) window.clearTimeout(brainLeaveTimer.current);
    brainLeaveTimer.current = window.setTimeout(() => {
      setBrainMenu(null);
      brainLeaveTimer.current = null;
    }, 350);
  }, []);

  const cancelBrainMenuClose = useCallback(() => {
    if (brainLeaveTimer.current) {
      window.clearTimeout(brainLeaveTimer.current);
      brainLeaveTimer.current = null;
    }
  }, []);

  const onRequesterMenu = useCallback((menu: ActivityMenuState | null) => {
    if (requesterLeaveTimer.current) {
      window.clearTimeout(requesterLeaveTimer.current);
      requesterLeaveTimer.current = null;
    }
    setSkillMenu(null);
    setPackagesMenu(null);
    setBrainMenu(null);
    setRequesterMenu(menu);
  }, []);

  const scheduleRequesterMenuClose = useCallback(() => {
    if (requesterLeaveTimer.current) window.clearTimeout(requesterLeaveTimer.current);
    requesterLeaveTimer.current = window.setTimeout(() => {
      setRequesterMenu(null);
      requesterLeaveTimer.current = null;
    }, 350);
  }, []);

  const cancelRequesterMenuClose = useCallback(() => {
    if (requesterLeaveTimer.current) {
      window.clearTimeout(requesterLeaveTimer.current);
      requesterLeaveTimer.current = null;
    }
  }, []);

  const onReplayRun = useCallback((skillId: string, run: SkillRunRow) => {
    const keys = edgeKeysForPath(run.path);
    const expiry = Date.now() + REPLAY_MS;
    const skill = skillById(skillId);
    setSkillMenu(null);
    setPackagesMenu(null);
    setRequesterMenu(null);
    setBrainMenu(null);
    setFocusedSkillId(skillId);
    setReplayEdgeKeys(new Set(keys));
    setHistoricReplay({
      skillId,
      label: skill?.label || skillId,
      accent: skill?.accent || '#2c3e2f',
      sentence: run.sentence,
      when: relativeTime(run.ts),
    });
    setPulses(prevMap => {
      const next = new Map(prevMap);
      for (const k of keys) next.set(k, expiry);
      next.set(`skill-${skillId}`, expiry);
      next.set('node-gbrain', expiry);
      if (run.path.sourceId) next.set(`node-src-${run.path.sourceId}`, expiry);
      if (run.path.deliveryId) next.set(`node-del-${run.path.deliveryId}`, expiry);
      if (run.path.pulseRequester) next.set('node-del-requester', expiry);
      return next;
    });
    if (replayTimerRef.current) window.clearTimeout(replayTimerRef.current);
    replayTimerRef.current = window.setTimeout(() => {
      setReplayEdgeKeys(null);
      setFocusedSkillId(null);
      setHistoricReplay(null);
      setPulses(prevMap => new Map(Array.from(prevMap).filter(([, exp]) => exp > Date.now())));
      replayTimerRef.current = null;
    }, REPLAY_MS + 200);
  }, []);

  const toLocalPoint = useCallback((clientX: number, clientY: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { x: clientX, y: clientY };
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const onEdgeHover = useCallback(
    (edgeId: string, clientX: number, clientY: number) => {
      setTooltip(prev => {
        if (prev?.locked) return prev;
        const p = toLocalPoint(clientX, clientY);
        return { edgeId, x: p.x, y: p.y, locked: false };
      });
    },
    [toLocalPoint],
  );

  const onEdgeLeave = useCallback((edgeId: string) => {
    setTooltip(prev => {
      if (!prev || prev.locked) return prev;
      if (prev.edgeId !== edgeId) return prev;
      return null;
    });
  }, []);

  const onEdgeTap = useCallback(
    (edgeId: string, clientX: number, clientY: number) => {
      const p = toLocalPoint(clientX, clientY);
      setTooltip(prev => {
        if (prev?.locked && prev.edgeId === edgeId) return null;
        return { edgeId, x: p.x, y: p.y, locked: true };
      });
    },
    [toLocalPoint],
  );

  const dismissTooltip = useCallback(() => setTooltip(null), []);

  const skillChips = useMemo(
    () =>
      FLOW_SKILLS.filter(s => s.id !== 'leads' || skillStates.has('leads')).map(s => {
        const st = skillStates.get(s.id);
        const runs = runsBySkill.get(s.id) || [];
        const lastTs = st?.lastTs || runs[0]?.ts || null;
        return {
          id: s.id,
          label: s.label,
          accent: s.accent,
          working: st?.working || false,
          glowUntil: liveUntilFor(`skill-${s.id}`),
          /** Replay / brain-search focus. */
          focused: highlightSkillId === s.id,
          lastSentence: st?.lastSentence || runs[0]?.sentence || null,
          lastTs,
          runs,
          packages: capabilitySkills?.get(s.id) || [],
        };
      }),
    [skillStates, liveUntilFor, highlightSkillId, runsBySkill, capabilitySkills],
  );

  const anyWorking = useMemo(
    () => Array.from(skillStates.values()).some(s => s.working),
    [skillStates],
  );

  const sourceCount = useCallback(
    (id: FlowSourceId): { count: number | null; label: string } => {
      if (entityCtx && !entityCtx.loading) {
        for (const [type, meta] of Object.entries(ARTIFACT_META) as Array<
          [ArtifactType, (typeof ARTIFACT_META)[ArtifactType]]
        >) {
          if (meta.sourceId === id && entityCtx.counts[type] !== undefined) {
            return {
              count: entityCtx.counts[type] ?? 0,
              label: `${meta.label} for ${lens?.mode === 'entity' ? lens.label : ''}`,
            };
          }
        }
        return { count: null, label: '' };
      }
      const n = viewCounts.bySource.get(id) || 0;
      return { count: n > 0 ? n : null, label: 'moments in the loaded window' };
    },
    [entityCtx, viewCounts, lens],
  );

  const edgeActive = useCallback(
    (key: string) =>
      liveUntilFor(key) > Date.now() ||
      (replayEdgeKeys ? replayEdgeKeys.has(key) : false) ||
      focusedEdgeKeys.has(key) ||
      tooltip?.edgeId === key,
    [liveUntilFor, replayEdgeKeys, focusedEdgeKeys, tooltip],
  );

  const makeEdgeData = useCallback(
    (key: string, accent: string, label?: string, live?: boolean): GlowEdgeData => {
      /** Historic-run path only — opening the run menu must not animate every past edge. */
      const historicUntil = replayEdgeKeys?.has(key) ? Date.now() + REPLAY_MS : 0;
      return {
        liveUntil: Math.max(liveUntilFor(key), historicUntil),
        locked: tooltip?.locked === true && tooltip.edgeId === key,
        accent,
        label,
        live,
        onHover: onEdgeHover,
        onLeave: onEdgeLeave,
        onTap: onEdgeTap,
      };
    },
    [liveUntilFor, replayEdgeKeys, tooltip, onEdgeHover, onEdgeLeave, onEdgeTap],
  );

  /** Latest activity per CRM category (pathItems are newest-first). */
  const crmSurfaceLatest = useMemo(() => {
    const map = new Map<FlowCrmSurfaceId, { sentence: string; ts: Timestamp | null }>();
    for (const { item, path } of pathItems) {
      if (path.sourceId !== 'crm' || !path.crmSurfaceId) continue;
      if (path.type === 'poll' || path.type === 'report') continue;
      if (map.has(path.crmSurfaceId)) continue;
      map.set(path.crmSurfaceId, { sentence: item.sentence, ts: item.ts });
    }
    return map;
  }, [pathItems]);

  const nodes: Node[] = useMemo(() => {
    let srcY = SRC_Y;
    const srcNodes: Node[] = FLOW_SOURCES.map(info => {
      const id = info.id as FlowSourceId;
      const c = sourceCount(id);
      const tick = tickers.bySource.get(id);
      const h = sourceCardHeight(id);
      const crmRows: CrmSurfaceRow[] | undefined =
        id === 'crm'
          ? FLOW_CRM_SURFACES.map(s => {
              const latest = crmSurfaceLatest.get(s.id);
              return {
                id: s.id,
                label: s.label,
                access: s.access,
                lastTs: latest?.ts || null,
                glowUntil: liveUntilFor(EDGE_IDS.crmSurfaceToBrain(s.id)),
              };
            })
          : undefined;
      const glowUntil =
        id === 'crm'
          ? Math.max(
              liveUntilFor(`node-src-${info.id}`),
              ...FLOW_CRM_SURFACES.map(s => liveUntilFor(EDGE_IDS.crmSurfaceToBrain(s.id))),
            )
          : Math.max(
              liveUntilFor(EDGE_IDS.sourceToBrain(id)),
              liveUntilFor(`node-src-${info.id}`),
            );
      const node: Node = {
        id: `src-${info.id}`,
        type: 'cfSource',
        position: { x: SRC_X, y: srcY },
        data: {
          info,
          count: c.count,
          countLabel: c.label,
          glowUntil,
          ticker: tick?.sentence || null,
          tickerTs: tick?.ts || null,
          crmRows,
        },
        draggable: false,
      };
      srcY += h + SRC_GAP;
      return node;
    });

    const brainGlowUntil = Math.max(
      liveUntilFor('node-gbrain'),
      liveUntilFor(EDGE_IDS.brainToAgent),
      ...FLOW_CRM_SURFACES.map(s => liveUntilFor(EDGE_IDS.crmSurfaceToBrain(s.id))),
      ...FLOW_SOURCES.filter(s => s.id !== 'crm').map(s =>
        liveUntilFor(EDGE_IDS.sourceToBrain(s.id as FlowSourceId)),
      ),
    );

    const brainNode: Node = {
      id: 'gbrain',
      type: 'cfBrain',
      position: { x: BRAIN_X, y: 200 },
      data: {
        info: GBRAIN_NODE,
        glowUntil: brainGlowUntil,
        inCount: viewCounts.brainIn,
        ticker: tickers.brain?.sentence || null,
        tickerTs: tickers.brain?.ts || null,
        moments: brainMoments,
        onBrainMenu,
        onBrainMenuLeave: scheduleBrainMenuClose,
        getCanvasEl: () => canvasRef.current,
      },
      draggable: false,
    };

    const requesterNode: Node = {
      id: 'del-requester',
      type: 'cfDelivery',
      position: { x: AGENT_X, y: REQ_Y },
      data: {
        info: FLOW_REQUESTER,
        count: viewCounts.byDelivery.get('requester') || null,
        glowUntil: Math.max(
          liveUntilFor(EDGE_IDS.agentToDelivery('requester')),
          liveUntilFor('node-del-requester'),
        ),
        ticker: tickers.byDelivery.get('requester')?.sentence || null,
        tickerTs: tickers.byDelivery.get('requester')?.ts || null,
        moments: requesterMoments,
        onRequesterMenu,
        onRequesterMenuLeave: scheduleRequesterMenuClose,
        getCanvasEl: () => canvasRef.current,
      },
      draggable: false,
    };

    const agentNode: Node = {
      id: 'agent',
      type: 'cfAgent',
      position: { x: AGENT_X, y: AGENT_Y },
      data: {
        info: AGENT_NODE,
        anyWorking,
        skills: skillChips,
        capabilitySkillsLoading: !!capabilitySkillsLoading,
        onSkillMenu,
        onSkillMenuLeave: scheduleSkillMenuClose,
        onCapabilitySkillsMenu,
        onCapabilitySkillsMenuLeave: schedulePackagesMenuClose,
        getCanvasEl: () => canvasRef.current,
      },
      draggable: false,
    };

    const delNodes: Node[] = FLOW_OUTPUTS.map((info, i) => {
      const tick = tickers.byDelivery.get(info.id as FlowDeliveryId);
      return {
        id: `del-${info.id}`,
        type: 'cfDelivery',
        position: { x: DEL_X, y: AGENT_Y + i * (DEL_H + DEL_GAP) },
        data: {
          info,
          count: viewCounts.byDelivery.get(info.id as FlowDeliveryId) || null,
          glowUntil: Math.max(
            liveUntilFor(EDGE_IDS.agentToDelivery(info.id as FlowDeliveryId)),
            liveUntilFor(`node-del-${info.id}`),
          ),
          ticker: tick?.sentence || null,
          tickerTs: tick?.ts || null,
        },
        draggable: false,
      };
    });

    return [...srcNodes, brainNode, requesterNode, agentNode, ...delNodes];
  }, [
    sourceCount,
    liveUntilFor,
    viewCounts,
    tickers,
    anyWorking,
    skillChips,
    capabilitySkillsLoading,
    brainMoments,
    requesterMoments,
    crmSurfaceLatest,
    onSkillMenu,
    scheduleSkillMenuClose,
    onCapabilitySkillsMenu,
    schedulePackagesMenuClose,
    onBrainMenu,
    scheduleBrainMenuClose,
    onRequesterMenu,
    scheduleRequesterMenuClose,
  ]);

  const edges: Edge[] = useMemo(() => {
    const srcEdges: Edge[] = FLOW_SOURCES.flatMap(info => {
      if (info.id === 'crm') {
        return FLOW_CRM_SURFACES.map(s => {
          const key = EDGE_IDS.crmSurfaceToBrain(s.id);
          return {
            id: key,
            source: 'src-crm',
            sourceHandle: s.id,
            target: 'gbrain',
            type: 'glowFlow',
            data: makeEdgeData(key, info.accent, undefined, s.access === 'live'),
          } as Edge;
        });
      }
      const key = EDGE_IDS.sourceToBrain(info.id as FlowSourceId);
      return [
        {
          id: key,
          source: `src-${info.id}`,
          target: 'gbrain',
          type: 'glowFlow',
          data: makeEdgeData(key, info.accent),
        } as Edge,
      ];
    });

    const brainAgent: Edge = {
      id: EDGE_IDS.brainToAgent,
      source: 'gbrain',
      target: 'agent',
      targetHandle: 'in',
      type: 'glowFlow',
      data: makeEdgeData(EDGE_IDS.brainToAgent, '#1A1A1A', 'pulls context'),
    };

    // Inbound ask (and handback pulse) — Requesting teammate above → agent.
    const requestEdge: Edge = {
      id: EDGE_IDS.agentToDelivery('requester'),
      source: 'del-requester',
      sourceHandle: 'to-agent',
      target: 'agent',
      targetHandle: 'req',
      type: 'glowFlow',
      data: makeEdgeData(EDGE_IDS.agentToDelivery('requester'), FLOW_REQUESTER.accent),
    };

    const delEdges: Edge[] = FLOW_OUTPUTS.map(info => {
      const key = EDGE_IDS.agentToDelivery(info.id as FlowDeliveryId);
      return {
        id: key,
        source: 'agent',
        sourceHandle: `out-${info.id}`,
        target: `del-${info.id}`,
        type: 'glowFlow',
        data: makeEdgeData(key, info.accent),
      };
    });

    // Dim edges that aren't on the open/replay skill path.
    return [...srcEdges, brainAgent, requestEdge, ...delEdges].map(e => ({
      ...e,
      className: edgeActive(e.id)
        ? 'cf-edge-active'
        : pathFocusSkillId && !focusedEdgeKeys.has(e.id)
          ? 'cf-edge-dim'
          : undefined,
    }));
  }, [makeEdgeData, edgeActive, pathFocusSkillId, focusedEdgeKeys]);

  const tooltipPayload = tooltip ? edgeLatestByKey.get(tooltip.edgeId) : null;
  const tooltipHasSkill = tooltipPayload
    ? hasReportedSkill(tooltipPayload.item.params)
    : false;
  const tooltipSkill =
    tooltipHasSkill && tooltipPayload?.path.skillId
      ? skillById(tooltipPayload.path.skillId)
      : null;

  const canvasW = canvasRef.current?.clientWidth || wrapRef.current?.clientWidth || 400;
  const canvasH = canvasRef.current?.clientHeight || wrapRef.current?.clientHeight || 400;
  const menuWidth = 260;
  const menuLeft = skillMenu
    ? Math.min(Math.max(skillMenu.x, 8), canvasW - menuWidth - 8)
    : 0;
  const menuTop = skillMenu
    ? skillMenu.openUp
      ? undefined
      : Math.min(skillMenu.y + skillMenu.h + 6, canvasH - 12)
    : 0;
  const menuBottom = skillMenu?.openUp
    ? Math.max(canvasH - skillMenu.y + 6, 12)
    : undefined;

  const skillMenuRuns = (() => {
    if (!skillMenu) return [] as SkillRunRow[];
    const fromFeed = runsBySkill.get(skillMenu.skillId) || [];
    if (fromFeed.length) return fromFeed;
    const st = skillStates.get(skillMenu.skillId);
    if (!st?.lastSentence) return [];
    return [
      {
        id: `skill-state-${skillMenu.skillId}`,
        sentence: st.lastSentence,
        ts: st.lastTs,
        path: {
          type: 'retrieve' as const,
          skillId: skillMenu.skillId,
          sourceId: null,
          deliveryId: null,
        },
      },
    ];
  })();

  return (
    <div className="cf-wrap" ref={wrapRef}>
      <div
        className="cf-canvas"
        ref={canvasRef}
        onClick={e => {
          const t = e.target as HTMLElement | null;
          if (
            t?.closest(
              '.cf-skill-when, .cf-cap-skills-cell, .cf-skill-popover, .cf-packages-popover, .cf-brain, .cf-brain-popover, .cf-requester, .cf-requester-popover',
            )
          )
            return;
          if (tooltip?.locked) dismissTooltip();
          if (skillMenu) setSkillMenu(null);
          if (packagesMenu) setPackagesMenu(null);
          if (brainMenu) setBrainMenu(null);
          if (requesterMenu) setRequesterMenu(null);
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          minZoom={0.35}
          maxZoom={1.15}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          panOnDrag={false}
          panOnScroll={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <FitViewOnResize />
          <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#DBD8D0" />
        </ReactFlow>

        {skillMenu && (
          <div
            className={`cf-skill-popover cf-skill-popover-float${skillMenu.openUp ? ' up' : ''}`}
            role="dialog"
            aria-label={`${skillMenu.label} recent runs`}
            style={{
              ['--cf-accent' as string]: skillMenu.accent,
              left: menuLeft,
              top: menuTop,
              bottom: menuBottom,
              width: menuWidth,
            }}
            onMouseEnter={cancelSkillMenuClose}
            onMouseLeave={scheduleSkillMenuClose}
            onClick={e => e.stopPropagation()}
          >
            <div className="cf-skill-popover-head">{skillMenu.label} · runs</div>
            {skillMenuRuns.length === 0 ? (
              <p className="cf-skill-popover-empty">No runs in the loaded window.</p>
            ) : (
              <ul className="cf-skill-popover-list">
                {skillMenuRuns.map(run => {
                  const canReplay = edgeKeysForPath(run.path).length > 0;
                  return (
                    <li key={run.id}>
                      <button
                        type="button"
                        className="cf-skill-run"
                        disabled={!canReplay}
                        title={canReplay ? 'Replay path on canvas' : 'No path to replay'}
                        onClick={() => {
                          if (canReplay) onReplayRun(skillMenu.skillId, run);
                        }}
                      >
                        <span className="cf-skill-run-sentence">{run.sentence}</span>
                        {run.brainPath && (
                          <span className="cf-skill-run-brain" title={run.brainExcerpt || undefined}>
                            opened {run.brainPath}
                          </span>
                        )}
                        <span className="cf-skill-run-when">{relativeTime(run.ts)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {packagesMenu && (
          <div
            className={`cf-skill-popover cf-packages-popover cf-skill-popover-float${packagesMenu.openUp ? ' up' : ''}`}
            role="dialog"
            aria-label={`${packagesMenu.label} CompanyBrain skills`}
            style={{
              ['--cf-accent' as string]: packagesMenu.accent,
              left: Math.min(Math.max(packagesMenu.x, 8), canvasW - menuWidth - 8),
              top: packagesMenu.openUp
                ? undefined
                : Math.min(packagesMenu.y + packagesMenu.h + 6, canvasH - 12),
              bottom: packagesMenu.openUp
                ? Math.max(canvasH - packagesMenu.y + 6, 12)
                : undefined,
              width: menuWidth,
            }}
            onMouseEnter={cancelPackagesMenuClose}
            onMouseLeave={schedulePackagesMenuClose}
            onClick={e => e.stopPropagation()}
          >
            <div className="cf-skill-popover-head">{packagesMenu.label} · skills</div>
            {packagesMenu.packages.length === 0 ? (
              <p className="cf-skill-popover-empty">
                No CompanyBrain skills mapped to this capability yet.
              </p>
            ) : (
              <ul className="cf-skill-popover-list">
                {packagesMenu.packages.map(pkg => (
                  <li key={pkg.path} className="cf-package-row">
                    <span className="cf-package-name">{pkg.name}</span>
                    {pkg.description && (
                      <span className="cf-package-desc">{pkg.description}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {brainMenu && (
          <div
            className="cf-brain-popover"
            role="dialog"
            aria-label="Context moments in view"
            style={{
              left: Math.min(
                Math.max(brainMenu.x + brainMenu.w + 10, 8),
                canvasW - menuWidth - 8,
              ),
              top: Math.min(Math.max(brainMenu.y, 8), canvasH - 12),
              width: menuWidth,
            }}
            onMouseEnter={cancelBrainMenuClose}
            onMouseLeave={scheduleBrainMenuClose}
            onClick={e => e.stopPropagation()}
          >
            <div className="cf-brain-popover-head">
              Context moments in view
              <span className="cf-brain-popover-count">
                {viewCounts.brainIn > ACTIVITY_MOMENTS_CAP
                  ? `${ACTIVITY_MOMENTS_CAP} of ${viewCounts.brainIn}`
                  : viewCounts.brainIn}
              </span>
            </div>
            {brainMenu.moments.length === 0 ? (
              <p className="cf-brain-popover-empty">No context moments in the loaded window.</p>
            ) : (
              <ul className="cf-brain-popover-list">
                {brainMenu.moments.map(m => (
                  <li key={m.id} className="cf-brain-moment">
                    <div className="cf-brain-moment-meta">
                      <span className="cf-brain-moment-kind">{m.kind}</span>
                      {m.sourceLabel && (
                        <span className="cf-brain-moment-source">{m.sourceLabel}</span>
                      )}
                      <span className="cf-brain-moment-when">{relativeTime(m.ts)}</span>
                    </div>
                    <p className="cf-brain-moment-sentence">{m.sentence}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {requesterMenu && (
          <div
            className="cf-requester-popover"
            role="dialog"
            aria-label="Logged requesting-teammate activity"
            style={{
              left: Math.min(
                Math.max(requesterMenu.x + requesterMenu.w + 10, 8),
                canvasW - menuWidth - 8,
              ),
              top: Math.min(Math.max(requesterMenu.y, 8), canvasH - 12),
              width: menuWidth,
            }}
            onMouseEnter={cancelRequesterMenuClose}
            onMouseLeave={scheduleRequesterMenuClose}
            onClick={e => e.stopPropagation()}
          >
            <div className="cf-requester-popover-head">
              Logged activity
              <span className="cf-requester-popover-count">
                {(viewCounts.byDelivery.get('requester') || 0) > ACTIVITY_MOMENTS_CAP
                  ? `${ACTIVITY_MOMENTS_CAP} of ${viewCounts.byDelivery.get('requester')}`
                  : viewCounts.byDelivery.get('requester') || requesterMenu.moments.length}
              </span>
            </div>
            {requesterMenu.moments.length === 0 ? (
              <p className="cf-requester-popover-empty">No asks in the loaded window.</p>
            ) : (
              <ul className="cf-requester-popover-list">
                {requesterMenu.moments.map(m => (
                  <li key={m.id} className="cf-requester-moment">
                    <div className="cf-requester-moment-meta">
                      <span className="cf-requester-moment-kind">{m.kind}</span>
                      {m.sourceLabel && (
                        <span className="cf-requester-moment-skill">{m.sourceLabel}</span>
                      )}
                      <span className="cf-requester-moment-when">{relativeTime(m.ts)}</span>
                    </div>
                    <p className="cf-requester-moment-sentence">{m.sentence}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {historicReplay && (
          <div
            className="cf-historic-badge"
            role="status"
            aria-live="polite"
            style={{ ['--cf-accent' as string]: historicReplay.accent }}
          >
            <span className="cf-historic-badge-mark">
              <History size={14} strokeWidth={2.4} />
              Historic Run
            </span>
            <span className="cf-historic-badge-cap">{historicReplay.label}</span>
            <span className="cf-historic-badge-when">{historicReplay.when}</span>
          </div>
        )}

        <div className="cf-legend">
          <span className="cf-legend-item">
            <span className="cf-legend-input">
              <LogIn size={10} />
            </span>
            brain inputs
          </span>
          <span className="cf-legend-item">
            <span className="cf-legend-line idle" /> idle path
          </span>
          <span className="cf-legend-item">
            <span className="cf-legend-line live" /> live CRM pull
          </span>
          <span className="cf-legend-item">
            <span className="cf-legend-line flow" /> in use / historic
          </span>
          <span className="cf-legend-item cf-legend-hint">hover or tap a line for detail</span>
          {lens && (
            <span className="cf-legend-lens">
              scoped to <strong>{lens.mode === 'entity' ? lens.label : `“${lens.text}”`}</strong>
            </span>
          )}
        </div>

        {tooltip && tooltipPayload && (
          <div
            className={`cf-edge-tooltip${tooltip.locked ? ' locked' : ''}`}
            style={{
              left: Math.min(Math.max(tooltip.x + 12, 12), (wrapRef.current?.clientWidth || 400) - 280),
              top: Math.min(Math.max(tooltip.y + 12, 12), (wrapRef.current?.clientHeight || 400) - 160),
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="cf-edge-tooltip-type">
              {FLOW_TYPE_LABELS[tooltipPayload.path.type]}
              {tooltipSkill && <span className="cf-edge-tooltip-skill">{tooltipSkill.label}</span>}
              {!tooltipHasSkill && (
                <span className="cf-edge-tooltip-skill-missing">
                  <AlertTriangle size={10} />
                  no skill
                </span>
              )}
            </div>
            <p className="cf-edge-tooltip-sentence">{tooltipPayload.item.sentence}</p>
            {typeof tooltipPayload.item.params?.brainPath === 'string' && (
              <div className="cf-edge-tooltip-brain" title="CompanyBrain path opened during this run">
                opened {String(tooltipPayload.item.params.brainPath)}
              </div>
            )}
            {typeof tooltipPayload.item.params?.brainExcerpt === 'string' && (
              <p className="cf-edge-tooltip-brain-excerpt">
                {String(tooltipPayload.item.params.brainExcerpt)}
              </p>
            )}
            <div className="cf-edge-tooltip-crumb">{describePath(tooltipPayload.path)}</div>
            <div className="cf-edge-tooltip-meta">
              <span>{relativeTime(tooltipPayload.item.ts)}</span>
              {tooltipPayload.item.status && tooltipPayload.item.status !== 'ok' && (
                <span className={`cf-edge-tooltip-status status-${tooltipPayload.item.status}`}>
                  {tooltipPayload.item.status}
                </span>
              )}
              {tooltip.locked && <span className="cf-edge-tooltip-lock">tap elsewhere to dismiss</span>}
            </div>
          </div>
        )}
        {tooltip && !tooltipPayload && (
          <div
            className="cf-edge-tooltip"
            style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
            onClick={e => e.stopPropagation()}
          >
            <p className="cf-edge-tooltip-sentence">No recent transaction on this path.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BrainContextFlow: React.FC<BrainContextFlowProps> = React.memo(props => (
  <ReactFlowProvider>
    <BrainContextFlowInner {...props} />
  </ReactFlowProvider>
));

export default BrainContextFlow;
